import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MathJaxContext } from 'better-react-mathjax';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout';
import Courses from './screens/Courses';
import CourseDetail from './screens/CourseDetail';
import LessonResourceDetail from './screens/LessonResourceDetail';
import LessonResources from './screens/LessonResources';
import Login from './screens/Login';
import Register from './screens/Register';
import Welcome from './screens/Welcome';
import Dashboard from './screens/Dashboard';
import Chat from './screens/Chat';
import Upload from './screens/Upload';
import Quiz from './screens/Quiz';
import QuestionSetDetail from './screens/QuestionSetDetail';
import AIUsage from './screens/AIUsage';
import AIUsageHistory from './screens/AIUsageHistory';
import EditQuestion from './screens/EditQuestion';
import VerifyEmail from './screens/VerifyEmail';
import AgentChat from './components/AgentChat';
import UsersDashboard from './screens/UsersDashboard';
import InstituteAdminDashboard from './screens/InstituteAdminDashboard';
import ImportLMS from './screens/ImportLMS';
import AuditResults from './screens/AuditResults';
import LecturePlayer from './screens/LecturePlayer';

import ProtectedRoute from './ProtectedRoute';
import { useAuth } from './AuthContext';
import authService from './services/authService';

function App() {
  const { user, selectedRole } = useAuth();

  const redirectPath = user && selectedRole ? authService.getRoleRedirectPath(selectedRole) : '/courses';

  const mathJaxConfig = {
    tex: {
      inlineMath: [['\\(', '\\)']],
      displayMath: [['\\[', '\\]']],
    },
  };

  return (
    <MathJaxContext config={mathJaxConfig}>
    <Router>
      <Routes>
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/login" element={user ? <Navigate to={redirectPath} /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to={redirectPath} /> : <Register />} />
        
        <Route path="/" element={
          user ? <Navigate to={redirectPath} /> : <Navigate to="/welcome" />
        } />
        
        <Route path="/" element={<Layout />}>
          <Route path="/courses" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <Courses />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <Dashboard />
            </ProtectedRoute>
          } />          
          <Route path="/users-dashboard" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <UsersDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin-dashboard" element={
            <ProtectedRoute allowedRoles={['institute_admin']}>
              <InstituteAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/chat" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <Chat />
            </ProtectedRoute>
          } />
          <Route path="/upload" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <Upload />
            </ProtectedRoute>
          } />
          <Route path="/quiz" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <Quiz />
            </ProtectedRoute>
          } />  
          <Route path="/course/:id" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <CourseDetail />
            </ProtectedRoute>
          } />
          <Route path="/lessons/:lessonId/resources" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <LessonResources />
            </ProtectedRoute>
          } />
          <Route path="/lessons/:lessonId/resource/:resourceId" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <LessonResourceDetail/>
            </ProtectedRoute>
          } />
          <Route path="/question-sets/:questionSetId" element={<QuestionSetDetail />} />
          <Route path="/ai-usage" element={<AIUsage />} />
          <Route path="/ai-usage-history" element={<AIUsageHistory />} />
          <Route path="/questions/:questionId/edit" element={<EditQuestion />} />
          <Route path="/agent-chat" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <AgentChat />
            </ProtectedRoute>
          } />
          <Route path="/import-lms" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <ImportLMS />
            </ProtectedRoute>
          } />
          <Route path="/audit-results/:id" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <AuditResults />
            </ProtectedRoute>
          } />
          <Route path="/audit-results/qs/:qsId" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <AuditResults />
            </ProtectedRoute>
          } />
          <Route path="/lecture-player/:id" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <LecturePlayer />
            </ProtectedRoute>
          } />
          {/* Add other protected routes as needed */}
        </Route>
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Router>
    </MathJaxContext>
  );
}

export default App;
